// Guest passwords for the Room Door Lock (DeviceID 102) — the three create
// screens, laid out after the Tuya lock app the product is modelled on:
//
//   One-Time Password       "Obtain Password", valid 6 hours, one use
//   Time-Limited Password   "Obtain Password" between two date-times
//   Scheduled Password      a typed 7-digit code, a validity range, and an
//                           optional daily window on chosen weekdays
//
// Everything the lock understands lives in core/devices/lockPasswords — the
// command words there are PROVISIONAL until the firmware's are handed over.
//
// ⚠️ THE CODE IS SHOWN WITH THE LOCK'S ANSWER, NEVER AS "SAVED". A publish is not
// a save: an offline lock drops it silently and the guest would stand at a
// locked door holding a code the lock never heard of. So the result view says
// waiting → confirmed / refused / no answer, and only "confirmed" is green.
//
// Every code is SAVED ON THE SERVER first (LockPassword, a slot 0..99) and then
// sent to the lock with that slot; the Active passwords list below reads the
// server and deletes the same way round. See core/devices/lockPasswords.
//
// ⚠️ EVERY CODE IS BEHIND THE SAME CHECK AS UNLOCK — a code opens the door just
// as Unlock does. `verify` comes from DoorScreen: the phone's fingerprint prompt,
// or the 4-digit app PIN where there is no fingerprint. Nothing is generated or
// sent until it resolves true.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Dialog, IconButton, Button, TextField, Switch, CircularProgress,
  List, ListItemButton, ListItemIcon, ListItemText, useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PasswordIcon from '@mui/icons-material/Password';
import TimerIcon from '@mui/icons-material/Timer';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Snackbar from '@mui/material/Snackbar';
import { useApp } from '../../../../../core/state/AppContext';
import { publishCommand } from '../../../../../core/utils/deviceController';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener, parseStatus,
} from '../../../../../core/mqtt/mqttClient';
import { toLocalInput } from '../../../../../core/devices/phase';
import {
  PASSWORD_KIND as K, WEEKDAYS, ONE_TIME_HOURS, GENERATED_LENGTH, SCHEDULED_LENGTH,
  REPLY_TIMEOUT_MS, ALL_DAYS_MASK, randomDigits, parseLockReply,
  validatePassword, oneTimeWindow, toServerRow, lockSetCommand, lockDeleteCommand,
  saveLockPassword, deleteLockPassword, getLockPasswords, kindOf, KIND_LABEL, stateOf, windowText,
} from '../../../../../core/devices/lockPasswords';
import { PHONE_SHEET } from '../../../../shared/fitDialog';

const TITLES = {
  [K.ONE_TIME]: 'Create One-Time Password',
  [K.TIME_LIMITED]: 'Create Time-Limited Password',
  [K.SCHEDULED]: 'Create Scheduled Password',
};

const ENTRIES = [
  { kind: K.ONE_TIME, label: 'One-Time Password', hint: `Valid ${ONE_TIME_HOURS} hours, one use`, icon: <PasswordIcon /> },
  { kind: K.TIME_LIMITED, label: 'Time-Limited Password', hint: 'Valid between two dates', icon: <TimerIcon /> },
  { kind: K.SCHEDULED, label: 'Scheduled Password', hint: 'Your own code, on chosen days and hours', icon: <EventRepeatIcon /> },
];

const fromInput = (s) => (s ? new Date(s) : null);
const plusHours = (d, h) => new Date(d.getTime() + h * 3600 * 1000);
const roundUpToHour = (d) => {
  const r = new Date(d);
  if (r.getMinutes() || r.getSeconds()) r.setHours(r.getHours() + 1);
  r.setMinutes(0, 0, 0);
  return r;
};
const fmt = (d) => d.toLocaleString(undefined, {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/*
 * Publish one password and wait for the lock's answer on `<uno>/client`.
 * -> { phase: 'idle'|'waiting'|'ok'|'fail'|'silent', send(command) }
 */
function useLockSend(uno, userId) {
  const [phase, setPhase] = useState('idle');
  const cleanup = useRef(null);

  const stop = () => { if (cleanup.current) { cleanup.current(); cleanup.current = null; } };
  useEffect(() => stop, []);

  const send = (command) => {
    stop();
    setPhase('waiting');
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const r = parseLockReply(parseStatus(payload));
      if (!r) return;           // an ordinary status frame, not our answer
      setPhase(r);
      stop();
    });
    const timer = setTimeout(() => { setPhase('silent'); stop(); }, REPLY_TIMEOUT_MS);
    cleanup.current = () => { clearTimeout(timer); off(); unsubscribeTopic(topic); };
    publishCommand(uno, command, userId);
  };

  return { phase, send };
}

function Row({ label, children, c, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        minHeight: 56, px: 2, borderBottom: `1px solid ${c.text}22`,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Typography sx={{ color: c.text, fontSize: 16, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

// A native date/time picker dressed as a row value — the webview's own picker
// is the most usable one on a phone.
function PickerInput({ type, value, onChange, c }) {
  return (
    <>
      <TextField
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="standard"
        InputProps={{ disableUnderline: true }}
        inputProps={{ style: { textAlign: 'right', color: c.text, fontSize: 15 } }}
        sx={{ minWidth: 0 }}
      />
      <ChevronRightIcon sx={{ color: `${c.text}88` }} />
    </>
  );
}

function Masked({ c }) {
  return (
    <Box sx={{
      my: 3, py: 2.5, textAlign: 'center',
      backgroundImage: `repeating-linear-gradient(0deg, ${c.text}14 0 1px, transparent 1px 4px)`,
    }}
    >
      <Typography sx={{ color: c.text, fontSize: 28, letterSpacing: 6 }}>
        {'*'.repeat(GENERATED_LENGTH)}
      </Typography>
    </Box>
  );
}

// The code, once sent, with the lock's answer beside it.
function Result({ result, phase, onDone, c }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.code); setCopied(true); } catch { /* select by hand */ }
  };
  const state = {
    waiting: { icon: <CircularProgress size={20} />, text: 'Sending to the lock…', color: c.text },
    ok: { icon: <CheckCircleIcon sx={{ color: '#2e7d32' }} />, text: 'Lock confirmed. The password is ready to use.', color: '#2e7d32' },
    fail: { icon: <ErrorIcon sx={{ color: '#c62828' }} />, text: 'The lock refused this password. Do not give it out.', color: '#c62828' },
    silent: {
      icon: <HelpIcon sx={{ color: '#ed6c02' }} />,
      text: 'The lock did not answer. It may be offline. Test the password at the door before giving it out.',
      color: '#ed6c02',
    },
  }[phase] || { icon: null, text: '', color: c.text };

  return (
    <Box sx={{ px: 2.5, pb: 3 }}>
      <Typography sx={{ color: `${c.text}99`, fontSize: 13, mt: 1 }}>
        Share this code with your guest. It is also listed under Active passwords.
      </Typography>
      <Box sx={{ my: 3, py: 2.5, textAlign: 'center', border: `1px solid ${c.text}33`, borderRadius: 2 }}>
        <Typography sx={{
          color: c.text, fontSize: 30, fontWeight: 600, letterSpacing: 4,
          fontVariantNumeric: 'tabular-nums', userSelect: 'all', wordBreak: 'break-all',
        }}
        >
          {result.code}
        </Typography>
        {result.name ? <Typography sx={{ color: `${c.text}99`, fontSize: 13 }}>{result.name}</Typography> : null}
      </Box>
      <Typography sx={{ color: c.text, fontSize: 14 }}>
        Valid from {fmt(result.from)}
        <br />
        until {fmt(result.until)}
        {result.window ? (<><br />{result.window}</>) : null}
        {result.kind === K.ONE_TIME ? (<><br />Can be used only once.</>) : null}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2.5 }}>
        {state.icon}
        <Typography sx={{ color: state.color, fontSize: 14 }}>{state.text}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
        <Button variant="outlined" onClick={copy} sx={{ flex: 1, borderRadius: 5 }}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="contained" onClick={onDone} sx={{ flex: 1, borderRadius: 5 }}>Done</Button>
      </Box>
    </Box>
  );
}

function CreatePasswordDialog({ kind, device, onClose, verify, onSaved }) {
  const theme = useTheme();
  const c = theme.sli;
  const { userId } = useApp();
  const { phase, send } = useLockSend(device?.UniqueNumber, userId);

  const now = new Date();
  const start = roundUpToHour(now);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [from, setFrom] = useState(toLocalInput(kind === K.SCHEDULED ? now : start));
  const [until, setUntil] = useState(kind === K.SCHEDULED ? '' : toLocalInput(plusHours(start, 1)));
  const [repeat, setRepeat] = useState(false);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('09:00');
  const [dayMask, setDayMask] = useState(ALL_DAYS_MASK);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [checking, setChecking] = useState(false);
  const submit = async () => {
    if (checking) return;
    const p = kind === K.ONE_TIME
      ? { kind, code: randomDigits(GENERATED_LENGTH), ...oneTimeWindow() }
      : {
        kind,
        code: kind === K.SCHEDULED ? code : randomDigits(GENERATED_LENGTH),
        from: fromInput(from),
        until: fromInput(until),
        repeat: kind === K.SCHEDULED && repeat,
        startTime,
        endTime,
        dayMask,
      };
    const err = validatePassword(p);
    if (err) { setError(err); return; }
    setError('');
    if (verify) {
      setChecking(true);
      const ok = await verify(`Create a guest password for ${device?.DeviceName || 'the door'}`);
      setChecking(false);
      if (!ok) return;
    }
    // 1. the server keeps it and hands out the slot; 2. the lock is told.
    setChecking(true);
    const row = toServerRow({ ...p, name }, device?.UniqueNumber, userId);
    const saved = await saveLockPassword(row);
    setChecking(false);
    if (!saved.ok) { setError(saved.message); return; }
    if (onSaved) onSaved();
    send(lockSetCommand(saved.id, row));
    setResult({
      ...p,
      slot: saved.id,
      name: name.trim(),
      window: p.repeat
        ? `${startTime}–${endTime} on ${WEEKDAYS.filter((_, i) => dayMask & (1 << i)).join(', ')}`
        : '',
    });
  };

  const toggleDay = (i) => setDayMask((m) => m ^ (1 << i));
  const scheduled = kind === K.SCHEDULED;

  return (
    <Dialog
      open
      onClose={onClose}
      {...PHONE_SHEET}
      PaperProps={{ sx: { ...PHONE_SHEET.PaperProps.sx, bgcolor: c.back } }}
    >
      {/* Header: Cancel/Save for the typed form, a back arrow for "Obtain". */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1, pt: 0.5, minHeight: 44,
      }}
      >
        {scheduled && !result ? (
          <>
            <Button onClick={onClose} sx={{ color: c.text, fontSize: 17, textTransform: 'none' }}>Cancel</Button>
            <Button onClick={submit} sx={{ fontSize: 17, textTransform: 'none' }}>Save</Button>
          </>
        ) : (
          <IconButton onClick={onClose} aria-label="Back"><ArrowBackIosNewIcon sx={{ color: c.text }} /></IconButton>
        )}
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 500, px: 2.5 }}>
          {TITLES[kind]}
        </Typography>

        {result ? (
          <Result result={result} phase={phase} onDone={onClose} c={c} />
        ) : (
          <>
            <Typography sx={{ color: `${c.text}99`, fontSize: 13, px: 2.5, mt: 1 }}>
              Save the password immediately. The password is not displayed in the app.
            </Typography>

            {scheduled ? (
              <Box sx={{ px: 2.5, mt: 3 }}>
                <Typography sx={{ color: c.text, fontSize: 15, mb: 1 }}>{SCHEDULED_LENGTH}-Digit Password</Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, minHeight: 88,
                  borderRadius: 2, bgcolor: `${c.text}0d`, border: `1px solid ${c.text}1f`,
                }}
                >
                  <TextField
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, SCHEDULED_LENGTH))}
                    variant="standard"
                    placeholder={'0'.repeat(SCHEDULED_LENGTH)}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{
                      inputMode: 'numeric',
                      style: { color: c.text, fontSize: 24, letterSpacing: 4 },
                    }}
                    sx={{ flex: 1 }}
                  />
                  <Button onClick={() => setCode(randomDigits(SCHEDULED_LENGTH))} sx={{ textTransform: 'none', flexShrink: 0 }}>
                    Generate Randomly
                  </Button>
                </Box>
              </Box>
            ) : (
              <Masked c={c} />
            )}

            {scheduled ? (
              <Box sx={{ mt: 3 }}>
                <Row label="Password Name" c={c}>
                  <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    variant="standard"
                    placeholder="(Optional) Enter the name."
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ style: { textAlign: 'right', color: c.text } }}
                  />
                </Row>
              </Box>
            ) : null}

            {kind !== K.ONE_TIME ? (
              <>
                <Row label="Effective Time" c={c}>
                  <PickerInput type="datetime-local" value={from} onChange={setFrom} c={c} />
                </Row>
                <Row label="Expiration Time" c={c}>
                  <PickerInput type="datetime-local" value={until} onChange={setUntil} c={c} />
                </Row>
              </>
            ) : null}

            {scheduled ? (
              <Box sx={{ mt: 2 }}>
                <Row label="Repeat" c={c}>
                  <Switch checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />
                </Row>
                {repeat ? (
                  <>
                    <Row label="Start Time" c={c}>
                      <PickerInput type="time" value={startTime} onChange={setStartTime} c={c} />
                    </Row>
                    <Row label="End Time" c={c}>
                      <PickerInput type="time" value={endTime} onChange={setEndTime} c={c} />
                    </Row>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, px: 2, py: 2 }}>
                      {WEEKDAYS.map((d, i) => {
                        const on = Boolean(dayMask & (1 << i));
                        return (
                          <Box
                            key={d}
                            onClick={() => toggleDay(i)}
                            sx={{
                              width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', userSelect: 'none', fontSize: 14,
                              bgcolor: on ? 'primary.main' : 'transparent',
                              color: on ? 'primary.contrastText' : c.text,
                              border: on ? 'none' : `1px solid ${c.text}55`,
                            }}
                          >
                            {d}
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                ) : null}
              </Box>
            ) : null}

            {kind === K.ONE_TIME ? (
              <Typography sx={{ color: `${c.text}aa`, fontSize: 14, textAlign: 'center', px: 3 }}>
                The one-time password is valid for {ONE_TIME_HOURS} hours and can be used only once before it expires.
              </Typography>
            ) : null}
            {kind === K.TIME_LIMITED ? (
              <Typography sx={{ color: `${c.text}aa`, fontSize: 14, textAlign: 'center', px: 3, mt: 3 }}>
                The password works only between the effective and expiration time.
              </Typography>
            ) : null}

            {error ? (
              <Typography sx={{ color: '#c62828', fontSize: 14, textAlign: 'center', px: 3, mt: 2 }}>{error}</Typography>
            ) : null}

            {!scheduled ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
                <Button variant="contained" onClick={submit} sx={{ borderRadius: 6, px: 6, py: 1.5, fontSize: 17, textTransform: 'none' }}>
                  Obtain Password
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </Box>
    </Dialog>
  );
}

// The entry list shown under the keypad on a lock that supports it.
export default function LockPasswords({ device, verify }) {
  const theme = useTheme();
  const c = theme.sli;
  const [open, setOpen] = useState(null);
  const { userId } = useApp();
  const uno = device?.UniqueNumber;
  const [rows, setRows] = useState(undefined);   // undefined loading, null unavailable
  const [toast, setToast] = useState('');

  const reload = useCallback(async () => {
    setRows(await getLockPasswords(uno));
  }, [uno]);
  useEffect(() => { reload(); }, [reload]);

  // Delete: same identity check as creating; server first (frees the slot), then
  // the lock. The lock's answer is not awaited here — the list is the server's.
  const remove = async (row) => {
    if (verify && !(await verify('Delete a guest password'))) return;
    const ok = await deleteLockPassword(uno, row.id);
    if (!ok) { setToast('Could not delete. Try again.'); return; }
    publishCommand(uno, lockDeleteCommand(row.id), userId);
    setToast('Deleted, and the lock was told to forget it.');
    reload();
  };

  const when = (sec) => (Number(sec) ? fmt(new Date(Number(sec) * 1000)) : '—');
  const STATE = { active: ['Active', '#2e7d32'], upcoming: ['Not yet valid', '#ed6c02'], expired: ['Expired', '#9e9e9e'] };

  return (
    <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', px: 2, pb: 3 }}>
      <Typography sx={{ color: `${c.text}aa`, fontSize: 13, px: 1, mb: 0.5 }}>Active passwords</Typography>
      <Box sx={{ borderRadius: 2, border: `1px solid ${c.text}22`, mb: 2, overflow: 'hidden' }}>
        {rows === undefined && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}
        {rows === null && (
          <Typography sx={{ color: `${c.text}99`, fontSize: 13, p: 2 }}>Could not load the list from the server.</Typography>
        )}
        {Array.isArray(rows) && rows.length === 0 && (
          <Typography sx={{ color: `${c.text}99`, fontSize: 13, p: 2 }}>No guest passwords yet.</Typography>
        )}
        {Array.isArray(rows) && rows.map((row) => {
          const [label, colour] = STATE[stateOf(row)];
          const win = windowText(row);
          return (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${c.text}14` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: c.text, fontSize: 15, fontWeight: 600, letterSpacing: 2, fontVariantNumeric: 'tabular-nums' }}>
                  {row.code}
                  <Typography component="span" sx={{ color: colour, fontSize: 12, fontWeight: 700, letterSpacing: 0, ml: 1 }}>{label}</Typography>
                </Typography>
                <Typography sx={{ color: `${c.text}99`, fontSize: 12 }}>
                  {`${KIND_LABEL[kindOf(row)]} · ${when(row.startEpoch)} → ${when(row.endEpoch)}`}
                  {win ? <><br />{win}</> : null}
                </Typography>
              </Box>
              <IconButton aria-label="Delete password" onClick={() => remove(row)}>
                <DeleteOutlineIcon sx={{ color: c.text }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ color: `${c.text}aa`, fontSize: 13, px: 1, mb: 0.5 }}>Guest passwords</Typography>
      <List disablePadding sx={{ borderRadius: 2, border: `1px solid ${c.text}22`, overflow: 'hidden' }}>
        {ENTRIES.map((e) => (
          <ListItemButton key={e.kind} onClick={() => setOpen(e.kind)} divider>
            <ListItemIcon sx={{ color: c.text, minWidth: 40 }}>{e.icon}</ListItemIcon>
            <ListItemText
              primary={e.label}
              secondary={e.hint}
              primaryTypographyProps={{ sx: { color: c.text } }}
              secondaryTypographyProps={{ sx: { color: `${c.text}99` } }}
            />
            <ChevronRightIcon sx={{ color: `${c.text}88` }} />
          </ListItemButton>
        ))}
      </List>
      {open ? <CreatePasswordDialog kind={open} device={device} verify={verify} onSaved={reload} onClose={() => setOpen(null)} /> : null}
      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
